import type { WaitlistQuestionnaire as ApiWaitlistQuestionnaire } from '@/types';

export interface WaitlistQuestionnaireDisplay {
  useCases: string[];
  roles: string[];
  socialPlatform: string;
  socialHandle: string;
  social: string;
  teamSize: string;
  primaryText: string;
  secondaryText: string;
}

const USE_CASE_LABELS: Record<string, string> = {
  product_prototyping: '产品原型设计',
  ui_design_dev_handoff: 'UI 设计与开发交付',
  design_variants_exploration: '设计方案探索',
  personal_projects_portfolio: '个人项目或作品集',
  other: '其他',
};

const ROLE_LABELS: Record<string, string> = {
  product_designer: '产品设计师',
  frontend_developer: '前端开发者',
  product_manager: '产品经理',
  solo_developer: '独立开发者 / Indie Hacker',
  founder: '创始人',
  student: '学生',
  other: '其他',
};

const SOCIAL_PLATFORM_LABELS: Record<string, string> = {
  x: 'X / Twitter',
  xiaohongshu: '小红书',
  instagram: 'Instagram',
  weibo: '微博',
  wechat_official_account: '公众号',
  jike: '即刻',
  linkedin: '领英',
  other: '其他',
};

const TEAM_SIZE_LABELS: Record<string, string> = {
  solo: 'solo',
  '2_5': '2-5',
  '6_20': '6-20',
  '20_plus': '20+',
};

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const item of value) {
    const text = normalizeString(item);
    if (!text || seen.has(text)) {
      continue;
    }
    seen.add(text);
    normalized.push(text);
  }

  return normalized;
}

function isQuestionnaireEmpty(questionnaire: ApiWaitlistQuestionnaire): boolean {
  return (
    questionnaire.use_cases.length === 0 &&
    questionnaire.roles.length === 0 &&
    !questionnaire.use_case_other_text &&
    !questionnaire.role_other_text &&
    !questionnaire.social_platform &&
    !questionnaire.social_platform_other_text &&
    !questionnaire.social_handle &&
    !questionnaire.team_size
  );
}

function normalizeQuestionnaire(
  questionnaire?: Partial<ApiWaitlistQuestionnaire> | null,
): ApiWaitlistQuestionnaire | null {
  if (!questionnaire || typeof questionnaire !== 'object') {
    return null;
  }

  const normalized: ApiWaitlistQuestionnaire = {
    use_cases: normalizeStringArray(questionnaire.use_cases),
    use_case_other_text: normalizeString(questionnaire.use_case_other_text),
    roles: normalizeStringArray(questionnaire.roles),
    role_other_text: normalizeString(questionnaire.role_other_text),
    social_platform: normalizeString(questionnaire.social_platform),
    social_platform_other_text: normalizeString(
      questionnaire.social_platform_other_text,
    ),
    social_handle: normalizeString(questionnaire.social_handle),
    team_size: normalizeString(questionnaire.team_size),
  };

  return isQuestionnaireEmpty(normalized) ? null : normalized;
}

function expandOptionLabels(
  values: string[],
  labels: Record<string, string>,
  otherText?: string,
): string[] {
  return values.map((value) => {
    const baseLabel = labels[value] ?? value;
    if (value === 'other' && otherText) {
      return `${baseLabel}：${otherText}`;
    }
    return baseLabel;
  });
}

function formatSocialHandle(handle?: string): string {
  const normalizedHandle = (handle ?? '').trim().replace(/^@+/, '');
  return normalizedHandle ? `@${normalizedHandle}` : '';
}

function formatSocialPlatform(questionnaire: ApiWaitlistQuestionnaire): string {
  if (questionnaire.social_platform === 'other') {
    return questionnaire.social_platform_other_text || '其他';
  }
  return (
    SOCIAL_PLATFORM_LABELS[questionnaire.social_platform ?? ''] ??
    questionnaire.social_platform ??
    ''
  );
}

export function parseWaitlistQuestionnaire(
  rawReason: string,
  questionnaire?: ApiWaitlistQuestionnaire | null,
): ApiWaitlistQuestionnaire | null {
  const normalizedQuestionnaire = normalizeQuestionnaire(questionnaire);
  if (normalizedQuestionnaire) {
    return normalizedQuestionnaire;
  }

  const reasonText = rawReason.trim();
  if (!reasonText || !reasonText.startsWith('{')) {
    return null;
  }

  try {
    const parsed = JSON.parse(reasonText) as Partial<ApiWaitlistQuestionnaire>;
    return normalizeQuestionnaire(parsed);
  } catch {
    return null;
  }
}

export function getWaitlistQuestionnaireDisplay(
  questionnaire?: ApiWaitlistQuestionnaire | null,
): WaitlistQuestionnaireDisplay | null {
  if (!questionnaire) {
    return null;
  }

  const useCases = expandOptionLabels(
    questionnaire.use_cases,
    USE_CASE_LABELS,
    questionnaire.use_case_other_text,
  );
  const roles = expandOptionLabels(
    questionnaire.roles,
    ROLE_LABELS,
    questionnaire.role_other_text,
  );
  const socialPlatform = formatSocialPlatform(questionnaire);
  const socialHandle = formatSocialHandle(questionnaire.social_handle);
  const social = [socialPlatform, socialHandle].filter(Boolean).join(' ');
  const teamSize =
    TEAM_SIZE_LABELS[questionnaire.team_size ?? ''] ?? questionnaire.team_size ?? '';

  return {
    useCases,
    roles,
    socialPlatform,
    socialHandle,
    social,
    teamSize,
    primaryText: [
      useCases.length > 0 ? `使用目的：${useCases.join('、')}` : '',
      roles.length > 0 ? `身份：${roles.join('、')}` : '',
    ]
      .filter(Boolean)
      .join(' | '),
    secondaryText: [
      social ? `社媒：${social}` : '',
      teamSize ? `团队规模：${teamSize}` : '',
    ]
      .filter(Boolean)
      .join(' | '),
  };
}
